import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const location = useLocation();

  // Check if current path is admin-related
  const isAdminPath = location.pathname.startsWith('/admin');

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to home page after logout
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <nav className="relative z-30 border-b border-gray-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex space-x-7">
            <div>
              <a href="#home" className="flex items-center px-2 py-3 font-serif text-2xl text-aegean-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600">
                <span>Rizes</span>
              </a>
            </div>
            {/* Public links only make sense on the one-page site, hide them on /admin */}
            {!isAdminPath && (
              <div className="hidden items-center space-x-1 md:flex">
                <a
                  href="#home"
                  className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:text-aegean-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                >
                  Home
                </a>
                <a
                  href="#gallery"
                  className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:text-aegean-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                >
                  Gallery
                </a>
                <a
                  href="#contact"
                  className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:text-aegean-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                >
                  Contact
                </a>
                <a
                  href="#book"
                  className="ml-2 rounded border border-aegean-blue px-4 py-2 text-sm font-medium text-aegean-blue transition-colors hover:bg-aegean-blue hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                >
                  Book Now
                </a>
              </div>
            )}
          </div>

          <div className="hidden items-center space-x-3 md:flex">
            {isAdminPath && (
              <a
                href="/"
                className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-primary-500 hover:text-white"
              >
                View Website
              </a>
            )}
            {currentUser ? (
              <>
                <Link
                  to="/admin/dashboard"
                  className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-primary-500 hover:text-white"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-primary-500 hover:text-white"
                >
                  Logout
                </button>
              </>
            ) : (
              isAdminPath && (
                <Link
                  to="/admin/login"
                  className="rounded px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-primary-500 hover:text-white"
                >
                  Admin Login
                </Link>
              )
            )}
          </div>

          <div className="flex items-center md:hidden">
            <button onClick={toggleMenu} aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={isOpen} className="mobile-menu-button rounded p-2 text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600">
              {isOpen ? (
                <X className="h-6 w-6 text-gray-500" />
              ) : (
                <Menu className="h-6 w-6 text-gray-500" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className={`${isOpen ? 'block' : 'hidden'} border-t border-gray-100 bg-white md:hidden`}>
        <div className="mx-auto max-w-7xl space-y-1 px-4 py-3 sm:px-6">
          {isAdminPath && (
            <a
              href="/"
              onClick={closeMenu}
              className="block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              View Website
            </a>
          )}
          {!isAdminPath && (
            <>
              <a
                href="#home"
                onClick={closeMenu}
                  className="block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Home
              </a>
              <a
                href="#gallery"
                onClick={closeMenu}
                  className="block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Gallery
              </a>
              <a
                href="#contact"
                onClick={closeMenu}
                  className="block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Contact
              </a>
              <a
                href="#book"
                onClick={closeMenu}
                className="block py-2 px-3 text-gray-500 hover:bg-primary-500 hover:text-white"
              >
                Book Now
              </a>
            </>
          )}
          {currentUser ? (
            <>
              <Link
                to="/admin/dashboard"
                onClick={closeMenu}
                  className={`block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                  location.pathname === '/admin/dashboard' ? 'bg-primary-500 text-white' : ''
                }`}
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Logout
              </button>
            </>
          ) : (
            isAdminPath && (
              <Link
                to="/admin/login"
                onClick={closeMenu}
                  className={`block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                  location.pathname === '/admin/login' ? 'bg-primary-500 text-white' : ''
                }`}
              >
                Admin Login
              </Link>
            )
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;